import { ISubAccountData } from "@/store/SubAccountStore";
import { Button, Checkbox, Drawer, Radio } from "@renderbus/ui";
import { Fragment, useState } from "react";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import { TextButton } from "@/pages/child-customer/atom";
import styled from "styled-components";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import request from "project-config/services/request/request";
import { useSubAccountStore } from "@/store/SubAccountStore";
import QuestionIcon from "@/assets/icons/common/permission-question";
import { RadioChangeEvent, message } from "antd";
import { Tooltip } from "@renderbus/ui/components/tooltip";
import { ToolTip } from "task-eva-ui";
import { getScrollbarStyle } from "project-config/style";

export interface IOption {
  type: PermissionCategories;
  label: string;
  value: number;
  key: string;
  help?: string;
  children?: IOption[];
}

export interface IKeyPermissionNames {
  [key: string]: {
    view: string;
    viewAndEdit: string;
  };
}

export enum PermissionCategories {
  Assets = "user_com_private_span_assets",
  Task = "user_com_private_span_task",
  Deduction = "user_com_private_span_deduction",
  BalanceOrRechargeRecord = "user_com_private_span_balance_or_recharge_record",
  Bill = "user_com_private_span_bill",
  SoftwareConfiguration = "user_com_private_span_software_configuration",
}

export function EditPermissionButton(row: ISubAccountData) {
  const t = getTranslateNsT("center");
  const { fetchSubAccountList, fetchDingTalkSubAccountList, isSearchSubUser } =
    useSubAccountStore();
  const initOptions: IOption[] = [
    {
      type: PermissionCategories.Assets,
      label: t("user_com_childManage_privilege_property"),
      value: row["shareMainCapital"],
      key: "shareMainCapital",
      help: t("user_com_private_span_property"),
    },
    {
      type: PermissionCategories.Assets,
      label: t("user_com_childManage_privilege_del_assets"),
      value: row["subDeleteCapital"],
      key: "subDeleteCapital",
    },
    {
      type: PermissionCategories.Task,
      label: t("user_com_childManage_privilege_del_task"),
      value: row["subDeleteTask"],
      key: "subDeleteTask",
    },
    {
      type: PermissionCategories.Task,
      label: t("user_com_private_label_enable_adjust_task_level"),
      value: row["groupTaskLevelView"] || row["groupTaskLevel"] ? 1 : 0,
      key: "groupTaskLevelManage",
      help: t("user_com_private_span_adjust_task_level_tips"),
      children: [
        {
          type: PermissionCategories.Task,
          label: t("user_com_private_label_enable_adjust_check_task_level"),
          value: row["groupTaskLevelView"],
          key: "groupTaskLevelView",
          help: t("user_com_private_span_adjust_check_task_level_tips"),
        },
        {
          type: PermissionCategories.Task,
          label: t("user_com_private_label_enable_adjust_edit_task_level"),
          value: row["groupTaskLevel"],
          key: "groupTaskLevel",
          help: t("user_com_private_span_adjust_edit_task_level_tips"),
        },
      ],
    },
    {
      type: PermissionCategories.Task,
      label: t("user_com_childManage_privilege_sub_full_speed"),
      value: row["subFullSpeed"],
      key: "subFullSpeed",
    },
    {
      type: PermissionCategories.Task,
      label: t("user_com_private_label_enable_adjust_render_task"),
      value:
        row["inheritMainRenderingJobSet"] ||
        row["inheritMainRenderingJobSetView"]
          ? 1
          : 0,
      key: "inheritMainRenderingJob",
      help: t("user_com_private_span_adjust_render_task_tip"),
      children: [
        {
          type: PermissionCategories.Task,
          label: t("user_com_private_label_enable_adjust_check_render_task"),
          value: row["inheritMainRenderingJobSetView"],
          key: "inheritMainRenderingJobSetView",
          help: t("user_com_private_span_adjust_check_render_task_tips"),
        },
        {
          type: PermissionCategories.Task,
          label: t("user_com_private_label_enable_adjust_edit_render_task"),
          value: row["inheritMainRenderingJobSet"],
          key: "inheritMainRenderingJobSet",
          help: t("user_com_private_span_adjust_check_edit_task_tips"),
        },
      ],
    },
    {
      type: PermissionCategories.Deduction,
      label: t("user_com_childManage_privilege_use_balance"),
      value: row["useMainBalance"],
      key: "useMainBalance",
      help: t("user_com_private_span_balance_sub"),
    },
    {
      type: PermissionCategories.Deduction,
      label: t("user_com_childManage_privilege_hide_balance"),
      value: row["hideBalance"],
      key: "hideBalance",
      help: t("user_com_private_span_hide_balance"),
    },
    {
      type: PermissionCategories.BalanceOrRechargeRecord,
      label: t("user_com_childManage_privilege_hide_job_charge"),
      value: row["hideJobCharge"],
      key: "hideJobCharge",
      help: t("user_com_private_span_hide_job_charge"),
    },
    {
      type: PermissionCategories.Bill,
      label: t("user_com_private_label_enable_adjust_consumption_bill"),
      value: row["inheritMainConsumptionBillSet"],
      key: "inheritMainConsumptionBillSet",
      help: t("user_com_private_span_adjust_consumption_bill_tips"),
    },
    {
      type: PermissionCategories.SoftwareConfiguration,
      label: t("user_com_private_label_enable_adjust_soft_setting"),
      value: row["inheritMainSoftSet"],
      key: "inheritMainSoftSet",
      help: t("user_com_private_span_adjust_soft_setting_tips"),
    },
  ];

  const childrenOptions = initOptions
    .map((item) => item.children)
    .filter(Boolean)
    .flat() as IOption[];
  const initParam = [...initOptions, ...childrenOptions].reduce(
    (res, cur) => {
      res[cur.key] = cur.value;
      return res;
    },
    { id: row["id"] } as any
  );
  const sortOptionsFn = (options: IOption[]) =>
    Object.values(PermissionCategories).map((type) => ({
      type,
      content: options.filter((optionItem) => optionItem.type === type),
    }));

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<IOption[]>(initOptions);
  const [sortOptions, setSortOptions] = useState<
    { type: PermissionCategories; content: IOption[] }[]
  >(sortOptionsFn(initOptions));
  const [param, setParam] = useState(initParam);

  function handleChange(event: CheckboxChangeEvent) {
    const PermissionsKeyNames: IKeyPermissionNames = {
      inheritMainRenderingJob: {
        view: "inheritMainRenderingJobSetView",
        viewAndEdit: "inheritMainRenderingJobSet",
      },
      groupTaskLevelManage: {
        view: "groupTaskLevelView",
        viewAndEdit: "groupTaskLevel",
      },
    };
    const item = event.target.value as IOption;
    const { key, value } = item;
    const checked = !value;
    if (PermissionsKeyNames[key]) {
      const { view, viewAndEdit } = PermissionsKeyNames[key];
      if (checked) {
        setParam({ ...param, [view]: Number(checked) });
        const newOptions = options.map((item) =>
          item.key === key
            ? {
                ...item,
                value: Number(checked),
                children: item.children?.map((childrenItem) =>
                  item.key === view
                    ? { ...childrenItem, value: Number(checked) }
                    : childrenItem
                ),
              }
            : { ...item }
        );
        setOptions(newOptions);
        setSortOptions(sortOptionsFn(newOptions));
        return;
      } else {
        setParam({
          ...param,
          [view]: Number(checked),
          [viewAndEdit]: Number(checked),
        });
        const newOptions = options.map((item) =>
          item.key === key
            ? {
                ...item,
                value: Number(checked),
                children: item.children?.map((childrenItem) => ({
                  ...childrenItem,
                  value: Number(checked),
                })),
              }
            : { ...item }
        );
        setOptions(newOptions);
        setSortOptions(sortOptionsFn(newOptions));
        return;
      }
    }
    if (key === "useMainBalance" && checked && row.credit > 0) {
      message.warning(t("user_binfo_perfect_btn_save_tips"));
      return;
    }
    if (
      checked &&
      ["hideJobCharge", "inheritMainConsumptionBillSet"].includes(key)
    ) {
      let warningMsg = "";
      if (key === "hideJobCharge" && param["inheritMainConsumptionBillSet"]) {
        warningMsg = "user_com_private_span_uncheck_consumption";
      }
      if (key === "inheritMainConsumptionBillSet" && param["hideJobCharge"]) {
        warningMsg = "user_com_private_span_uncheck_hide_job_charge";
      }
      if (warningMsg) {
        message.warning(t(warningMsg));
        return;
      }
    }
    setParam({ ...param, [key]: Number(checked) });
    const option = options.find((item) => item.key === key);
    option && (option.value = Number(!value));
    setOptions(options);
    setSortOptions(sortOptionsFn(options));
  }

  const handleChangeRadio = (event: RadioChangeEvent, parentKey: string) => {
    const key = event.target.value;
    const checked = event.target.checked;

    const obj: { [key in string]: number } = {};
    options
      .find((item) => item.key === parentKey)
      ?.children?.forEach((item) => {
        obj[item.key] = 0;
      });

    setParam({ ...param, ...obj, [key]: Number(checked) });
    const newOptions = options.map((item) =>
      item.key === parentKey
        ? {
            ...item,
            children: item.children?.map((childrenItem) => ({
              ...childrenItem,
              value:
                childrenItem.key === key ? Number(checked) : Number(!checked),
            })),
          }
        : item
    );
    setOptions(newOptions);
    setSortOptions(sortOptionsFn(newOptions));
  };

  function handleCancel() {
    setOptions(initOptions);
    setParam(initParam);
    setSortOptions(sortOptionsFn(initOptions));
    setOpen(false);
  }

  async function handleSubmit() {
    const res = await request.post("/api/rendering/user/editSubUserAuth", {
      ...param,
      id: row.id,
    });
    if (res.result) {
      setOpen(false);
      message.success(t("user_com_modify_label_success_msg"));
      if (row?.isDingTalk) {
        fetchDingTalkSubAccountList({
          deptId: row?.deptId,
          subUserId: isSearchSubUser ? row?.id : undefined,
        });
        return;
      }
      fetchSubAccountList();
    } else if (res.code === 1003) {
      message.warning(t("user_tip_message_1003"));
    }
  }

  return (
    <div>
      <ToolTip
        content={t("user_com_childManage_del_render_btn_tip1")}
        trigger="hover"
        hidden={Boolean(row.status)}
      >
        <TextButton
          type="text"
          onClick={() => setOpen(true)}
          disabled={Boolean(!row.status)}
        >
          {t("user_com_childManage_del_render_btn_edit_permission")}
        </TextButton>
      </ToolTip>
      <Drawer
        open={open}
        size={"large"}
        title={t("user_com_childManage_del_render_btn_edit_permission")}
        width="600px"
        drawerContent={
          <Wrapper>
            <ContentWrapper>
              {sortOptions.map((typeItem) => (
                <Fragment key={typeItem.type}>
                  <div>{t(typeItem.type)}</div>
                  <CheckboxWrapper>
                    {typeItem.content?.map((item) => (
                      <Fragment key={item.key}>
                        <Checkbox
                          onChange={handleChange}
                          key={item.key}
                          checked={Boolean(item.value)}
                          value={item}
                        >
                          <CheckboxInner>
                            {item.label}
                            {item.help && (
                              <Tooltip
                                title={item.help}
                                color={"#202124"}
                                tooltipText={<QuestionIcon />}
                                getPopupContainer={() => document.body}
                              />
                            )}
                          </CheckboxInner>
                        </Checkbox>
                        {item.children?.length && (
                          <Radio.Group
                            onChange={(e) => handleChangeRadio(e, item.key)}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              flexWrap: "wrap",
                            }}
                            value={
                              item.children.find(
                                (childrenItem) => param[childrenItem.key]
                              )?.key
                            }
                          >
                            {item.children.map((childrenItem) => (
                              <Radio
                                style={{ width: "fit-content" }}
                                value={childrenItem.key}
                                disabled={
                                  !item.children?.find(
                                    (item) => param[item.key]
                                  )?.key
                                }
                              >
                                <CheckboxInner>
                                  {childrenItem.label}
                                  <Tooltip
                                    title={childrenItem.help}
                                    color={"#202124"}
                                    tooltipText={<QuestionIcon />}
                                    getPopupContainer={() => document.body}
                                  />
                                </CheckboxInner>
                              </Radio>
                            ))}
                          </Radio.Group>
                        )}
                      </Fragment>
                    ))}
                  </CheckboxWrapper>
                </Fragment>
              ))}
            </ContentWrapper>
            <ButtonWrapper>
              <Button
                style={{ width: "136px", height: "40px" }}
                onClick={handleCancel}
              >
                {t("user_com_private_btn_cancel")}
              </Button>
              <Button
                style={{ width: "136px", height: "40px" }}
                type="primary"
                onClick={handleSubmit}
              >
                {t("user_com_private_btn_comfirm")}
              </Button>
            </ButtonWrapper>
          </Wrapper>
        }
        onClose={handleCancel}
      />
    </div>
  );
}
const Wrapper = styled.div`
  position: relative;
  padding: 12px 24px;
  height: 100%;
  box-sizing: border-box;
`;
const ContentWrapper = styled.div`
  height: calc(100% - 60px);
  overflow-y: scroll;
  ${getScrollbarStyle()};
`;
const CheckboxInner = styled.div`
  display: flex;
  align-items: center;
  width: max-content;
  gap: 4px;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px 0 15px;
  label {
    width: 32%;
    margin: 0 5px 10px 0 !important;
  }
`;
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  position: absolute;
  bottom: 24px;
  width: calc(100% - 48px);
`;
